import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/kelly';

export enum ChartColors {
	BAR_BLUE = "#0069d1",
	BAR_BLUE_30 = "#8fd2ff",
	LABEL_GREY_1 = "#525564",
	LABEL_GREY_2 = "#7c7e8a",
	BACKGROUND_WHITE = "#ffffff",
	LABEL_BLACK = "#525564",
	SOLID_BLUE = "#66c2ff",
	BAR_GREEN = "#11ad30",
	BAR_RED = "#db3f27",
	PRIME_BLUE = "#002566",
	BAR_ORANGE = "#fa8e00",
	BAR_DARK_TURQUOISE = "#11d6cf",
	BAR_GREY = "#e4e4e6"
}

export enum ChartFonts {
	TEXT_14 = 14,
	TEXT_12 = 12,
	TEXT_10 = 10,
}

export enum BarOrientation {
	HORIZONTAL = 'HORIZONTAL',
	VERTICAL = 'VERTICAL',
}

export interface IThemeLabel {
	template: IThemeTemplate;
}

export interface IThemeTemplate {
	fill?: string;
	fontSize?: string;
	stroke?: string;
	horizontalCenter?: string;
	verticalCenter?: string;
	dx?: number;
	dy?: number;
	width?: number;
	interactionsEnabled?: boolean;
	truncate?: boolean;
	minWidth?: number;
	maxWidth?: number;
	align?: string;
}

export interface IChartThemes {
	categoryAxis: { labels: IThemeLabel };
	valueAxis: { labels: IThemeLabel; titleFill: string };
	series: Array<{
		columns?: IThemeLabel;
		labels: IThemeTemplate;
		color?: string;
		strokeDasharray?: string;
		fillOpacity?: number;
		stroke?: number;
	}>;
	cursorTooltipEnabled?: boolean;
}

export interface IAxisGrid {
	disabled?: boolean;
	location?: number;
}

export interface IValueAxisConfig {
	min?: number;
	extraMax?: number;
	grid?: IAxisGrid;
	maxPrecision?: number;
	labelFontSize?: number;
	label?: string;
	cursorTooltipEnabled?: boolean;
	hide?: boolean;
	numberFormatter?: string;
}

export interface ICategoryAxisConfig {
	autoGridCount?: boolean;
	inversed?: boolean;
	gridCount?: number;
	grid?: { template: IAxisGrid };
	labels: IThemeLabel;
	cellStartLocation?: number;
	cellEndLocation?: number;
	minGridDistance?: number;
	dataKey?: string;
	labelFontSize?: number;
	label?: string;
	cursorTooltipEnabled?: boolean;
	hide?: boolean;
}

export interface IValueStructure {
	prefixValue?: string | number;
	prefixprop?: string;
	value?: string | number;
	prop?: string;
	suffixValue?: string | number;
	suffixProp?: string;
	style?: string;
}

export interface ITooltip {
	tooltipText: Array<IValueStructure>;
	pointerOrientation: string;
	getFillFromObject: boolean;
	background: IThemeTemplate;
	label: IThemeTemplate;
}

export interface ISeriesConfig {
	stacked?: boolean;
	name: string;
	columns?: IThemeLabel;
	labels?: IThemeTemplate;
	tooltip?: ITooltip;
	chartType?: XYChartType;
	startLocation?: number;
	endLocation?: number;
}

export interface IDataParameters {
	valueAxis: IValueStructure;
	categoryAxis: IValueStructure;
	label: IValueStructure;
}

export interface IDataEntries {
	categoryAxisValue?: string | number;
	categoryAxisProp?: string;
	valueAxisValue?: string | number;
	valueAxisProp?: string;
	labelValue?: string | number;
	labelProp?: string;
	horizontalAxisDataKey: string;
	verticalAxisDataKey: string;
	labelDataKey?: string;
}

export interface IDataSort {
	key?: string;
	order: DataSortOrder;
}
export interface IDataConfig {
	parameters?: Array<IDataParameters>;
	dataPath?: string | Array<string>;
	dataEntries: Array<IDataEntries>;
	sort?: IDataSort;
}

export interface IChartLegend {
	position: string;
	contentAlign?: string;
	reverseOrder?: boolean;
}

export interface IChartCursor {
	maskBullets: boolean;
}

export interface IChartConfig {
	modifiedData: Array<any>;
	barOrientation: BarOrientation;
	tooltipHTMLArray: Array<string>;
	categoryAxisConfig: ICategoryAxisConfig;
	valueAxisConfig: IValueAxisConfig;
	seriesConfigArray: Array<ISeriesConfig>;
	themes: IChartThemes;
	dataEntries: Array<IDataEntries>;
	legend: IChartLegend;
	cursor: IChartCursor;
	shouldRotateLabels: boolean;
	componentHeight: string;
	horizontalScrollThreshold: number;
	supportDownload?: boolean;
}

export enum XYChartType {
	COLUMN_SERIES = 'COLUMN',
	LINE_SERIES = 'LINE',
	THRESHOLD = 'THRESHOLD',
}

export enum DataSortOrder {
	ASCENDING = 'ASCENDING',
	DESCENDING = 'DESCENDING',
}

export interface IXYChartConfig {
	barOrientation?: BarOrientation;
	data: IDataConfig;
	themes: IChartThemes;
	axisParameters: IAxisParameters;
	series: Array<ISeriesConfig>;
	legend?: IChartLegend;
	cursor?: IChartCursor;
	supportDownload?: boolean;
	horizontalScrollThreshold?: number;
}

export interface IXYChartProps {
	config: IChartConfig;
}
